import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';

const axiosInstance = axios.create({
  baseURL: API_URL,
  withCredentials: true,
});

axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export interface ExistingClaim {
  claimId: string;
  status: string;
}

export interface CreditAgreement {
  accountNumber: string;
  lenderName: string;
  accountType: string;
  accountTypeRaw?: string;
  startDate: string | null;
  endDate: string | null;
  currentBalance: number;
  monthlyPayment: number;
  currentStatus: string;
  repaymentTerm: string | null;
  paymentFrequency: string;
  bureau: string;
  sourcedFrom: string;
  reportId: number | string | null;
  searchDate: string | null;
  clientName: string | null;
  existingClaim: ExistingClaim | null;
  /** The claim reference the credit check was run under, e.g. "FC101963.001". */
  checkedUnder?: string | null;
  /** True when that reference is another claim of the same client. */
  checkedUnderSibling?: boolean;
}

export interface BureauStatus {
  /** False when the bureau's search list could not be read: show it, never as 0. */
  reachable: boolean;
  /** Checks that were found but whose report could not be loaded. */
  failedReports: number;
}

export interface CreditCheckLookupData {
  reference: string;
  clientName: string | null;
  searchDate: string | null;
  equifaxChecks: number;
  transunionChecks: number;
  totalAgreements: number;
  agreements: CreditAgreement[];
  bureaus?: { equifax: BureauStatus; transunion: BureauStatus };
  /** Sibling references whose checks are included. */
  siblingRefs?: string[];
}

export interface CreditCheckLookupResponse {
  success: boolean;
  found: boolean;
  data: CreditCheckLookupData;
  error?: string;
}

export const lookupCreditCheck = async (reference: string): Promise<CreditCheckLookupResponse> => {
  const response = await axiosInstance.post<CreditCheckLookupResponse>('/ui/credit-check/lookup', { reference });
  return response.data;
};
